import DriverBrowserNotice from '../DriverBrowserNotice'
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import MobileNavigation from './MobileNavigation'
import { MobileMenuProvider } from './MobileMenuContext'
import Sidebar from './Sidebar'
import { cabinetOfPath } from './navigation'
import './app-layout.css'
import { useMobileViewport } from '../../hooks/useMobileViewport'
import { useDriverWebTracking } from '../../hooks/useDriverWebTracking'
import { ApiError, api } from '../../lib/api'
import { isSolutionsPreviewPath } from '../../lib/solutionsPreview'

const publicLandingPaths = ['/', '/landing', '/otslezhivanie-bez-gps', '/for-logists', '/ot-voditelya']

// Standalone auth/account pages render without the app navigation (sidebar +
// mobile nav), the same way the login/landing page does — the user is not yet
// signed in here, so showing the guest sidebar with just a logo and logout
// button is confusing.
const standaloneAuthPaths = [
  '/register',
  '/forgot-password',
  '/password-reset-complete',
  '/email-confirmed',
  '/email-change-complete',
]

type AppLayoutProps = {
  children: ReactNode
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { authenticated, roles, activeRole, setActiveRole, logout } = useAuth()
  const role = activeRole || null
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const mobileWeb = useMobileViewport()
  const pendingReferralClaim = useRef<string | null>(null)
  const scrollerRef = useRef<HTMLElement | null>(null)
  const hasManagerRole = roles.includes('manager')
  // Unlike the native app, a browser can keep GPS only while this cabinet is open. The regular
  // desktop and mobile driver cabinets therefore share one tracker; the legacy invitation page
  // owns its own tracker while the acceptance flow is still on screen.
  useDriverWebTracking(role === 'driver' && !location.pathname.startsWith('/driver/accept'))

  useEffect(() => {
    const queryCode = new URLSearchParams(location.search).get('ref')?.trim()
    if (queryCode) localStorage.setItem('lupa_pending_ref', queryCode)
    if (!hasManagerRole) return
    const code = queryCode || localStorage.getItem('lupa_pending_ref')?.trim()
    if (!code || pendingReferralClaim.current === code) return

    pendingReferralClaim.current = code
    void api.claimReferral(code)
      .then(() => localStorage.removeItem('lupa_pending_ref'))
      .catch((claimError: unknown) => {
        // Invalid/self codes cannot recover on retry; network and server failures stay queued
        // so a later authenticated page load can finish attribution before first tracking.
        if (claimError instanceof ApiError && claimError.status >= 400 && claimError.status < 500 && claimError.status !== 401) {
          localStorage.removeItem('lupa_pending_ref')
        }
      })
      .finally(() => {
        pendingReferralClaim.current = null
      })
  }, [hasManagerRole, location.search])

  const switchCabinet = (next: string) => {
    setActiveRole(next)
    navigate(`/${next}`)
  }

  // The page decides the cabinet (navigation.cabinetOfPath): a link straight into the other
  // cabinet switches the menu and the switcher with it instead of mixing the two.
  const pathCabinet = cabinetOfPath(location.pathname)
  useEffect(() => {
    if (pathCabinet && pathCabinet !== activeRole && roles.includes(pathCabinet)) setActiveRole(pathCabinet)
  }, [activeRole, pathCabinet, roles, setActiveRole])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // The mobile cabinet reuses this scrolling element for every tab. Reset it
  // before the new route paints so a tab never inherits the previous tab's
  // position. Desktop navigation deliberately keeps its existing behaviour.
  useLayoutEffect(() => {
    if (mobileWeb && scrollerRef.current) scrollerRef.current.scrollTop = 0
  }, [location.pathname, mobileWeb])

  const showNavigation = !publicLandingPaths.includes(location.pathname) &&
    !isSolutionsPreviewPath(location.pathname) &&
    !standaloneAuthPaths.includes(location.pathname) &&
    !location.pathname.startsWith('/auth/') &&
    // Public share tracking (/track/:id) is opened by external customers without an account —
    // render it standalone, not inside the app chrome (sidebar links to cabinets they can't reach).
    !location.pathname.startsWith('/track/') &&
    // Legal documents (privacy/consent/terms) are public reference pages, opened from the
    // register form and the landing footer — no app chrome.
    !location.pathname.startsWith('/legal/') &&
    // A driver invite opened by someone not signed in yet: the welcome page and the sign-in prompt
    // after it are standalone, like /register — a guest sidebar would only offer «Выйти».
    !(location.pathname === '/driver/accept' && !authenticated) &&
    !location.pathname.startsWith('/404')

  if (!showNavigation) {
    return <>{children}</>
  }

  return (
    <MobileMenuProvider openMenu={() => setMobileMenuOpen(true)}>
      <div className="app-layout app-layout--with-navigation" data-mobile-web={mobileWeb ? 'true' : undefined}>
        <Sidebar
          role={role}
          pathname={location.pathname}
          onLogout={logout}
        />

        <MobileNavigation
          role={role}
          pathname={location.pathname}
          open={mobileMenuOpen}
          onToggle={() => setMobileMenuOpen((value) => !value)}
          onClose={() => setMobileMenuOpen(false)}
          onLogout={logout}
          roles={roles}
          activeRole={activeRole}
          onSwitchCabinet={switchCabinet}
        />

        <div className="app-layout__content">
          <section className="app-layout__surface" aria-label="Рабочая область">
            <main ref={scrollerRef} className="app-layout__scroller">
              {role === 'driver' && !location.pathname.startsWith('/driver/accept') ? <DriverBrowserNotice /> : null}
              {children}
            </main>
          </section>
        </div>
      </div>
    </MobileMenuProvider>
  )
}
